import { LinearProgress } from '@mui/material';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, NewConfirmDialog, RichEditorController } from 'components';
import { useCallbackPrompt } from 'hooks';

import { commentFieldRules, COMMENT_MAX_LENGTH } from './constants';
import { useAddGoalCommentForm } from './hooks';
import { ButtonsRow, CancelBtn, SubmitBtn } from './styled';
import { formStyles, loaderStyles, commentRichEditorStyles } from './styles';

interface AddGoalCommentFormProps {
  userId: string;
  achievementId: string;
  goalId: string;
}

export function AddGoalCommentForm({
  userId,
  achievementId,
  goalId,
}: AddGoalCommentFormProps): ReactElement {
  const { t } = useTranslation();

  const {
    isLoading,
    isMobile,
    isResetField,
    isDirty,
    isSubmitted,
    control,
    handleFormSubmit,
    reset,
  } = useAddGoalCommentForm({
    userId,
    achievementId,
    goalId,
  });

  const { showPrompt, confirmNavigation, cancelNavigation } = useCallbackPrompt(
    isDirty && !isSubmitted,
  );

  return (
    <>
      <Form sx={formStyles} onSubmit={handleFormSubmit}>
        {isLoading && <LinearProgress color="info" sx={loaderStyles} />}

        <RichEditorController
          name="content"
          control={control}
          placeholder={t('comments.post.placeholder')}
          maxLength={COMMENT_MAX_LENGTH}
          rules={commentFieldRules}
          sx={commentRichEditorStyles(isMobile)}
          isResetField={isResetField}
          withRightPadding
        />

        <ButtonsRow isPostComment>
          <CancelBtn color="inherit" variant="outlined" onClick={() => reset()}>
            {t('comments.cancelBtn')}
          </CancelBtn>
          <SubmitBtn color="info" variant="contained" type="submit" disabled={isLoading}>
            {t('comments.postBtn')}
          </SubmitBtn>
        </ButtonsRow>
      </Form>

      <NewConfirmDialog
        title={t('comments.modal.title')}
        subTitle={t('comments.modal.exit')}
        open={showPrompt}
        onClose={cancelNavigation}
        onConfirm={confirmNavigation}
        submitLabel={t('comments.modal.successButton')}
        cancelLabel={t('comments.modal.cancelButton')}
      />
    </>
  );
}
